import { Button, useTheme } from '@prio365/prio365-react-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, Dropdown, Menu } from 'antd';
import { useTranslation } from 'react-i18next';
import { PrioShareFileDto } from '../../../../../models/PrioShare';
import { PrioTheme } from '../../../../../theme/types';
import { makePrioStyles } from '../../../../../theme/utils';
import { getFileIcon, getFileMimeTypeKey } from '../utils';
import { apiDownloadPrioShareFiles } from '../../../api/prioShare';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: theme.borderRadius.small,
    cursor: 'pointer',
    userSelect: 'none' as const,
    transition: 'background 0.15s ease',
    '&:hover': {
      background: theme.colors.application.background.hover,
    },
  },
  fileItemSelected: {
    background: theme.colors.application.background.selected,
    '&:hover': {
      background: theme.colors.application.background.selected,
    },
  },
  fileName: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    maxWidth: 200,
  },
  iconSlot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  fileList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 4,
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '0px 8px',
    marginTop: 4,
  },
  removeButton: {
    marginLeft: 'auto',
    cursor: 'pointer',
    color: theme.colors.application.typography.muted,
    padding: '0 2px',
    '&:hover': {
      color: theme.colors.application.typography.default,
    },
  },
}));

const getFileIconColor = (filename: string, colors: PrioTheme['colors']): string => {
  const key = getFileMimeTypeKey(filename);
  return key ? colors.mimeType[key] : colors.application.typography.muted;
};

interface PrioShareSelectableFileListProps {
  files: PrioShareFileDto[];
  projectExtensionId: string;
  prioShareId: string;
  onRemove?: (fileId: string) => void;
}

const PrioShareSelectableFileList: React.FC<
  PrioShareSelectableFileListProps
> = ({ files, projectExtensionId, prioShareId, onRemove }) => {
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();

  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    new Set()
  );
  const [hoveredFileId, setHoveredFileId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const allChecked = files.length > 0 && selectedFileIds.size === files.length;
  const indeterminate = selectedFileIds.size > 0 && !allChecked;
  const canDownload = prioShareId && selectedFileIds.size > 0 && !isDownloading;

  const toggleFile = useCallback((fileId: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const handleCheckAll = useCallback(() => {
    if (allChecked) {
      setSelectedFileIds(new Set());
    } else {
      setSelectedFileIds(new Set(files.map((f) => f.prioShareFileId)));
    }
  }, [allChecked, files]);

  const handleDownloadSelected = useCallback(async () => {
    if (!canDownload) return;
    setIsDownloading(true);
    try {
      await apiDownloadPrioShareFiles(
        projectExtensionId,
        prioShareId,
        Array.from(selectedFileIds)
      );
    } catch {
      // error thrown by API — fetchWithRetry already retries
    } finally {
      setIsDownloading(false);
    }
  }, [canDownload, selectedFileIds, projectExtensionId, prioShareId]);

  const handleDownloadSingle = useCallback(
    async (fileId: string) => {
      if (!prioShareId) return;
      setIsDownloading(true);
      try {
        await apiDownloadPrioShareFiles(projectExtensionId, prioShareId, [fileId]);
      } catch {
        // error thrown by API — fetchWithRetry already retries
      } finally {
        setIsDownloading(false);
      }
    },
    [projectExtensionId, prioShareId]
  );

  useEffect(() => {
    setSelectedFileIds((prev) => {
      const validIds = new Set(files.map((f) => f.prioShareFileId));
      const next = new Set(Array.from(prev).filter((id) => validIds.has(id)));
      if (next.size !== prev.size) return next;
      return prev;
    });
  }, [files]);

  if (files.length === 0) return null;

  return (
    <>
      <div className={classes.fileList}>
        {files.map((file) => {
          const isSelected = selectedFileIds.has(file.prioShareFileId);
          return (
            <Dropdown
              key={file.prioShareFileId}
              overlay={
                <Menu>
                  <Menu.Item
                    onClick={() =>
                      handleDownloadSingle(file.prioShareFileId)
                    }
                  >
                    {t('common:widgetArea.prioShareWidget.download.download')}
                  </Menu.Item>
                </Menu>
              }
              trigger={['contextMenu']}
            >
              <div
                className={`${classes.fileItem} ${isSelected ? classes.fileItemSelected : ''}`}
                onClick={() => toggleFile(file.prioShareFileId)}
                onMouseEnter={() => setHoveredFileId(file.prioShareFileId)}
                onMouseLeave={() => setHoveredFileId(null)}
              >
                <span className={classes.iconSlot}>
                  {isSelected || hoveredFileId === file.prioShareFileId ? (
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleFile(file.prioShareFileId)}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={['fal', getFileIcon(file.alternativeFilename)]}
                      color={getFileIconColor(file.alternativeFilename, theme.colors)}
                    />
                  )}
                </span>
                <span className={classes.fileName} title={file.alternativeFilename}>
                  {file.alternativeFilename}
                </span>
                {onRemove && (
                  <span
                    className={classes.removeButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(file.prioShareFileId);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRemove(file.prioShareFileId);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={['fal', 'xmark']} />
                  </span>
                )}
              </div>
            </Dropdown>
          );
        })}
      </div>
      <div className={classes.actionBar}>
        <span className={classes.iconSlot}>
          <Checkbox
            checked={allChecked}
            indeterminate={indeterminate}
            onChange={handleCheckAll}
          />
        </span>
        {selectedFileIds.size > 0 && (
          <span
            style={{
              fontSize: theme.font.fontSize.small,
              color: theme.colors.application.typography.muted,
            }}
          >
            {selectedFileIds.size}{' '}
            {t('common:widgetArea.prioShareWidget.download.selected')}
          </span>
        )}
        <Button
          type="default"
          size="small"
          disabled={!canDownload}
          loading={isDownloading}
          onClick={handleDownloadSelected}
          iconProp={['fal', 'download']}
        >
          {t('common:widgetArea.prioShareWidget.download.download')}
        </Button>
      </div>
    </>
  );
};

export default PrioShareSelectableFileList;
